import { ArrowDropDownIcon } from "@rothoblaas/design-system";
import classNames from "classnames";
import { forwardRef, KeyboardEvent, useCallback, useId, useState } from "react";
import styles from "./ExpansionPanel.module.css";

interface Props {
  title: string;
  meta?: string;
  defaultExpanded?: boolean;
  /** Modalità controllata: se fornito, lo stato viene gestito dall'esterno via onToggle */
  expanded?: boolean;
  /** Callback per la modalità controllata */
  onToggle?: () => void;
  /** Header shown dimmed and not clickable/focusable; the panel stays collapsed. */
  disabled?: boolean;
  className?: string;
  children?: React.ReactNode;
}

const ExpansionPanel = forwardRef<HTMLDivElement, Props>(
  ({ title, meta, children, defaultExpanded = false, expanded, onToggle, disabled = false, className }, ref) => {
    const [internalExpanded, setInternalExpanded] = useState(defaultExpanded);
    const isControlled = expanded !== undefined;
    const isExpanded = !disabled && (isControlled ? expanded : internalExpanded);

    const panelId = useId();
    const headerId = `${panelId}-header`;
    const contentId = `${panelId}-content`;

    const toggle = useCallback(() => {
      if (disabled) {
        return;
      }

      if (isControlled) {
        onToggle?.();
      } else {
        setInternalExpanded((prev) => !prev);
      }
    }, [disabled, isControlled, onToggle]);

    const handleKeyDown = useCallback(
      (e: KeyboardEvent) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          toggle();
        }
      },
      [toggle],
    );

    return (
      <div ref={ref} className={classNames(styles.panel, className)}>
        <div
          id={headerId}
          className={classNames(styles.header, { [styles.headerDisabled]: disabled })}
          onClick={toggle}
          onKeyDown={handleKeyDown}
          role="button"
          tabIndex={disabled ? -1 : 0}
          aria-disabled={disabled || undefined}
          aria-expanded={isExpanded}
          aria-controls={contentId}
        >
          <ArrowDropDownIcon className={classNames(styles.chevron, { [styles.chevronExpanded]: isExpanded })} />
          <span className={styles.headerTitle}>{title}</span>
          {meta != null && <span className={styles.headerMeta}>{meta}</span>}
        </div>
        <div
          id={contentId}
          role="region"
          aria-labelledby={headerId}
          className={classNames(styles.bodyWrapper, { [styles.bodyWrapperExpanded]: isExpanded })}
        >
          <div className={styles.bodyInner}>
            <div className={styles.bodyContent}>{children}</div>
          </div>
        </div>
      </div>
    );
  },
);

ExpansionPanel.displayName = "ExpansionPanel";

export default ExpansionPanel;
